import { apiErrorMessage } from "../lib/apiError";
import { useState } from "react";
import { m as motion } from "framer-motion";
import { Send, CheckCircle2, CalendarCheck } from "lucide-react";
import { CONTACT } from "../lib/site";
import { track, getLeadAttribution } from "../lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

// Mirrors the server's BLOCKED_EMAIL_DOMAINS (kept in sync with /lib/blockedDomains if extracted).
const BLOCKED = new Set([
  "gmail.com","googlemail.com","yahoo.com","ymail.com","rocketmail.com",
  "hotmail.com","outlook.com","live.com","msn.com","aol.com",
  "icloud.com","me.com","mac.com","protonmail.com","proton.me","pm.me",
  "mail.com","gmx.com","gmx.us","yandex.com","yandex.ru","zoho.com",
  "fastmail.com","fastmail.fm","hey.com","tutanota.com","tutamail.com",
  "tutanota.de","mailbox.org","qq.com","163.com","126.com",
  "duck.com","duckduckgo.com",
]);

/**
 * Reusable inline lead-capture form for mid-page placement.
 * Targets the SAME backend as the spec-sheet flow but with no product slug —
 * captures an inquiry as an opt-in newsletter subscriber + sends a notification.
 *
 * Props:
 *  - eyebrow:   small uppercase label
 *  - headline:  bold prompt sentence (allow <em> via children if needed)
 *  - subhead:   supporting copy
 *  - testid:    data-testid root
 *  - theme:     "navy" (white form on navy band) | "bone" (navy form on light)
 */
export default function InlineLeadForm({
  eyebrow = "— Start a Conversation",
  headline,
  subhead,
  ctaLabel = "Notify Valor Team",
  testid = "inline-lead-form",
  theme = "navy",
}) {
  const [values, setValues] = useState({ name: "", email: "", organization: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const onChange = (e) => {
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));
    if (err) setErr("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const email = values.email.trim().toLowerCase();
    if (!values.name.trim() || !email) {
      setErr("Name and work email are required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr("Enter a valid email.");
      return;
    }
    const domain = email.split("@")[1];
    if (BLOCKED.has(domain)) {
      setErr("Business email required.");
      return;
    }

    setSubmitting(true);
    try {
      // Re-use the lead pipeline by submitting against any spec slug
      // we have — backend persists the lead even if email send fails.
      // For pure-inquiry inline forms we hit the newsletter+notification path.
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          source: testid,
          name: values.name.trim(),
          organization: values.organization.trim() || undefined,
          attribution: getLeadAttribution(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErr(apiErrorMessage(data.detail, "") || "Something went wrong. Try again.");
        return;
      }
      setDone(true);
      track.leadSubmit({ source: testid });
    } catch (_) {
      setErr("Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Theming ──────────────────────────────────────────────────────────
  const isDark = theme === "navy";
  const wrapBg = isDark ? "bg-navy text-white" : "bg-bone text-navy";
  const eyebrowColor = "text-signal";
  const headingColor = isDark ? "text-white" : "text-navy";
  const subColor = isDark ? "text-white/75" : "text-steel";
  const inputBg = isDark
    ? "bg-white/[0.06] border-white/15 focus:border-white text-white placeholder-white/45"
    : "bg-white border-line focus:border-navy text-navy placeholder-steel/60";
  const labelColor = isDark ? "text-white/75" : "text-signal";

  if (done) {
    return (
      <section
        className={`${wrapBg} relative overflow-hidden border-y border-line py-16 md:py-20`}
        data-testid={`${testid}-success`}
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-1 lg:flex hidden">
            <div className="w-12 h-12 border border-signal/40 bg-signal/10 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-signal" strokeWidth={1.7} />
            </div>
          </div>
          <div className="lg:col-span-11">
            <div className={`font-mono text-[14px] tracking-[0.32em] uppercase ${eyebrowColor} mb-3`}>
              — Message Received
            </div>
            <h3 className={`font-display font-bold text-2xl md:text-3xl ${headingColor} tracking-tight leading-[1.1]`}>
              Thank you for contacting Valor.{" "}
              <span className="text-signal italic font-light">
                We&apos;ll be in touch as soon as possible.
              </span>
            </h3>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`${wrapBg} relative overflow-hidden border-y border-line py-16 md:py-20`}
      data-testid={testid}
    >
      {isDark && <div className="absolute inset-0 grid-bg-dark opacity-20 pointer-events-none" />}
      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className={`font-mono text-[14px] tracking-[0.32em] uppercase ${eyebrowColor} mb-4`}>
            {eyebrow}
          </div>
          <h3 className={`font-display font-bold text-3xl md:text-4xl lg:text-[2.6rem] ${headingColor} leading-[1.05] tracking-tight`}>
            {headline}
          </h3>
          {subhead && (
            <p className={`mt-5 ${subColor} text-[17px] leading-[1.65] max-w-md`}>
              {subhead}
            </p>
          )}
        </div>

        <form onSubmit={onSubmit} className="lg:col-span-7 grid sm:grid-cols-12 gap-4" noValidate>
          <div className="sm:col-span-6">
            <label htmlFor={`${testid}-name`} className={`block font-mono text-[14px] tracking-[0.28em] uppercase ${labelColor} mb-2`}>
              Name *
            </label>
            <input
              id={`${testid}-name`}
              type="text"
              name="name"
              value={values.name}
              onChange={onChange}
              data-testid={`${testid}-input-name`}
              className={`w-full ${inputBg} border p-3 text-sm outline-none transition-colors`}
            />
          </div>
          <div className="sm:col-span-6">
            <label htmlFor={`${testid}-email`} className={`block font-mono text-[14px] tracking-[0.28em] uppercase ${labelColor} mb-2`}>
              Work Email *
            </label>
            <input
              id={`${testid}-email`}
              type="email"
              name="email"
              value={values.email}
              onChange={onChange}
              autoComplete="email"
              placeholder="you@yourcompany.com"
              data-testid={`${testid}-input-email`}
              className={`w-full ${inputBg} border p-3 text-sm outline-none transition-colors`}
            />
          </div>
          <div className="sm:col-span-12">
            <label htmlFor={`${testid}-org`} className={`block font-mono text-[14px] tracking-[0.28em] uppercase ${labelColor} mb-2`}>
              Organization
            </label>
            <input
              id={`${testid}-org`}
              type="text"
              name="organization"
              value={values.organization}
              onChange={onChange}
              placeholder="Program office / contractor"
              data-testid={`${testid}-input-org`}
              className={`w-full ${inputBg} border p-3 text-sm outline-none transition-colors`}
            />
          </div>
          {err && (
            <div className="sm:col-span-12 text-signal text-[17px] leading-[1.4]" data-testid={`${testid}-error`}>
              {err}
            </div>
          )}
          <div className="sm:col-span-12 flex flex-col-reverse sm:flex-row sm:items-stretch sm:justify-end gap-3">
            {CONTACT.bookingUrl ? (
              <a
                href={CONTACT.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${testid}-book-30min`}
                onClick={() => track.bookCallClick({ surface: testid })}
                className={`group inline-flex items-center justify-center gap-2.5 font-mono text-[15.5px] tracking-[0.28em] uppercase px-6 py-4 border transition-colors ${
                  isDark
                    ? "border-white/25 text-white hover:bg-white hover:text-navy hover:border-white"
                    : "border-navy/30 text-navy hover:bg-navy hover:text-white hover:border-navy"
                }`}
              >
                <CalendarCheck className="w-4 h-4 text-signal" strokeWidth={2} />
                <span>Book a 30-min Call</span>
              </a>
            ) : null}
            <motion.button
              type="submit"
              disabled={submitting}
              data-testid={`${testid}-submit`}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center justify-center gap-3 bg-signal hover:bg-white hover:text-signal disabled:bg-steel disabled:cursor-not-allowed text-white font-mono text-[15.5px] tracking-[0.32em] uppercase px-7 py-4 transition-colors group"
            >
              {submitting ? (
                <>
                  <span className="w-1.5 h-1.5 bg-white animate-pulse" />
                  Sending...
                </>
              ) : (
                <>
                  {ctaLabel}
                  <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                </>
              )}
            </motion.button>
          </div>
        </form>
      </div>
    </section>
  );
}
